import React, { useState, useMemo } from "react";
import T from '../../../components/common/T';
import { useInventory } from "../../../context/InventoryContext";
import { useProjects } from "../../../context/ProjectContext"; 
import { useCostSheets } from "../../../context/CostSheetContext"; 
import { useNavigate, useSearchParams } from "react-router-dom"; // 🟢 Added useSearchParams
import { toast } from "react-toastify";
import { ArrowLeftIcon, CurrencyDollarIcon } from "@heroicons/react/24/outline";
import "react-toastify/dist/ReactToastify.css";

export default function ScrapSalePage() {
  // 🟢 Brought in scrapList and updateRequisitionStatus
  const { inventories, sellScrap, getScrapStock, scrapList, updateRequisitionStatus } = useInventory();
  const { projects } = useProjects();
  const { costSheets } = useCostSheets(); 
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [form, setForm] = useState({
    scrapOrderId: searchParams.get("orderId") || "", // 🟢 Captures ID from URL if provided
    productId: "",
    quantity: "",
    price: "",
    buyer: "",
    note: "",
    projectId: "",
    costSheetId: "" 
  });

  const availableCostSheets = useMemo(() => {
      if (!form.projectId) return [];
      return costSheets.filter(cs => String(cs.projectId) === String(form.projectId));
  }, [costSheets, form.projectId]);

  const submit = async () => {
    if (!form.productId || !form.quantity || !form.price) return toast.warn("Please fill all required fields");

    const res = await sellScrap(form);
    
    if (res.success) {
        // 🟢 MAGIC FIX: If an order was linked, update its status!
        if (form.scrapOrderId) {
            await updateRequisitionStatus(form.scrapOrderId, "SOLD");
        }
        
        toast.success("Scrap Sold Successfully!");
        setTimeout(() => navigate("/admin/material/scrap"), 1500);
    } else {
        toast.error(res.message);
    }
  };

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen font-sans flex items-center justify-center">
        <div className="w-full max-w-2xl bg-white shadow-lg border border-gray-200 rounded-lg overflow-hidden">
            
            <div className="bg-gray-50 px-8 py-6 border-b border-gray-200 flex justify-between items-center">
                <div>
                    <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                        <CurrencyDollarIcon className="w-6 h-6 text-blue-600" />
                        Sell Scrap
                    </h2>
                    <p className="text-sm text-gray-500 mt-1"><T>Record sale of waste material to external buyer.</T></p>
                </div>
                <button onClick={() => navigate("/admin/material/scrap")} className="text-gray-400 hover:text-gray-600 transition">
                    <ArrowLeftIcon className="w-6 h-6" />
                </button>
            </div>

            <div className="p-8 space-y-6">
                
                {/* 🟢 NEW: LINK TO SOURCE ORDER */}
                <div className="bg-purple-50 border border-purple-100 p-4 rounded-md">
                    <label className="block text-xs font-bold text-purple-700 uppercase mb-1"><T>Link to Scrap Order (Optional)</T></label>
                    <select
                      className="w-full border border-purple-200 p-2.5 rounded bg-white focus:ring-2 focus:ring-purple-500 outline-none"
                      value={form.scrapOrderId}
                      onChange={e => setForm({ ...form, scrapOrderId: e.target.value })}
                    >
                      <option value=""><T>General Pool Sale (Do not link to specific order)</T></option>
                      {scrapList.filter(s => s.status === 'RECEIVED').map(s => (
                        <option key={s.id} value={s.id}>Order #{s.id} - {s.reference_number || s.id} ({s.employee_name})</option>
                      ))}
                    </select>
                    <p className="text-xs text-purple-600 mt-2">
                        * If linked, the specific order's status will automatically change to <b><T>SOLD</T></b>.
                    </p>
                </div>

                {/* 1. Item Selection */}
                <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Scrap Item to Sell</T></label>
                    <select
                      className="w-full border border-gray-300 p-2.5 rounded bg-white focus:ring-2 focus:ring-blue-500 outline-none transition"
                      value={form.productId}
                      onChange={e => setForm({ ...form, productId: e.target.value })}
                    >
                      <option value=""><T>Select Scrap Item...</T></option>
                      {inventories.map(i => {
                          const scrapQty = getScrapStock(i.id);
                          if (scrapQty <= 0) return null;
                          return (
                            <option key={i.id} value={i.id}>
                              {i.itemName} (Available: {scrapQty} {i.unit || 'Units'})
                            </option>
                          )
                      })}
                    </select>
                </div>

                {/* 2. Project Link */}
                <div className="bg-yellow-50 border border-yellow-100 p-4 rounded-md">
                    <label className="block text-xs font-bold text-yellow-700 uppercase mb-1"><T>Related Project (Optional)</T></label>
                    <div className="grid grid-cols-2 gap-4">
                        <select
                          className="w-full border border-yellow-200 p-2.5 rounded bg-white focus:ring-2 focus:ring-yellow-500 outline-none"
                          value={form.projectId}
                          onChange={e => setForm({ ...form, projectId: e.target.value, costSheetId: "" })}
                        >
                          <option value=""><T>General Sale (No specific project)</T></option>
                          {projects.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        
                        <select
                          className="w-full border border-yellow-200 p-2.5 rounded bg-white focus:ring-2 focus:ring-yellow-500 outline-none disabled:bg-gray-100 disabled:text-gray-400"
                          value={form.costSheetId}
                          onChange={e => setForm({ ...form, costSheetId: e.target.value })}
                          disabled={!form.projectId}
                        >
                          <option value="">{form.projectId ? "Select Job Cost Sheet" : "Select Project First"}</option>
                          {availableCostSheets.map(cs => (
                            <option key={cs.id} value={cs.id}>{cs.name}</option>
                          ))}
                        </select>
                    </div>
                    <p className="text-xs text-yellow-600 mt-2">
                        * If selected, a <b><T>Credit Entry</T></b> will be added to the Cost Sheet to reverse the scrap loss.
                    </p>
                </div>

                {/* 3. Sale Details */}
                <div className="grid grid-cols-2 gap-6">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Quantity to Sell</T></label>
                        <input
                          type="number"
                          className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                          placeholder="0.00"
                          value={form.quantity}
                          onChange={e => setForm({ ...form, quantity: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Sale Price Per Unit</T></label>
                        <div className="relative">
                            <span className="absolute left-3 top-2.5 text-gray-500">₹</span>
                            <input
                              type="number"
                              className="w-full border border-gray-300 p-2.5 pl-8 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                              placeholder="0.00"
                              value={form.price}
                              onChange={e => setForm({ ...form, price: e.target.value })}
                            />
                        </div>
                    </div>
                </div>

                {/* 4. Buyer Info */}
                <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Buyer / Customer Name</T></label>
                    <input
                      className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Enter buyer name..."
                      value={form.buyer}
                      onChange={e => setForm({ ...form, buyer: e.target.value })}
                    />
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
                    <button onClick={() => navigate("/admin/material/scrap")} className="px-5 py-2.5 border border-gray-300 rounded text-gray-600 font-bold text-xs uppercase hover:bg-gray-50 transition">
                        Cancel
                    </button>
                    <button onClick={submit} className="bg-blue-600 text-white px-6 py-2.5 rounded shadow-md hover:bg-blue-700 font-bold uppercase text-xs transition transform active:scale-95">
                        Confirm Sale
                    </button>
                </div>
            </div>
        </div>
      </div>
    </>
  );
}